import React, {
  useEffect,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  ArrowRight,
  Banknote,
  Check,
  CheckCircle2,
  CreditCard,
  Leaf,
  ReceiptText,
  Sparkles,
  Utensils,
} from "lucide-react";

import "./css/SuccessScreen.css";


function SuccessScreen() {

  const navigate = useNavigate();

  const location = useLocation();


  const {
    restaurantId,
    tableId,
  } = useParams();


  // =====================================================
  // SUCCESS DATA
  // =====================================================

  const {
    orderId,
    orderNumber,
    paymentMethod,
    total,
    paymentId,
  } = location.state || {};


  // =====================================================
  // HOME PATH
  // =====================================================

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  // =====================================================
  // AUTO REDIRECT
  // =====================================================

  useEffect(() => {

    const timer =
      setTimeout(() => {

        navigate(
          homePath,
          {
            replace: true,
          }
        );

      }, 10000);


    return () => {

      clearTimeout(timer);

    };

  }, [
    navigate,
    homePath,
  ]);


  // =====================================================
  // FORMAT PRICE
  // =====================================================

  const formattedTotal =
    Number(
      total || 0
    ).toFixed(2);


  // =====================================================
  // PAYMENT LABEL
  // =====================================================

  const paymentLabel =
    paymentMethod === "ONLINE"
      ? "Online Payment"
      : "Pay at Table";


  // =====================================================
  // BACK TO MENU
  // =====================================================

  const handleBackToMenu = () => {

    navigate(
      homePath,
      {
        replace: true,
      }
    );

  };


  // =====================================================
  // RENDER
  // =====================================================

  return (

    <main className="theme9-success-page">


      {/* =================================================
          DECORATIVE BACKGROUND
      ================================================= */}

      <div className="theme9-success-bg-shape theme9-success-bg-shape-one">
      </div>

      <div className="theme9-success-bg-shape theme9-success-bg-shape-two">
      </div>

      <div className="theme9-success-bg-shape theme9-success-bg-shape-three">
      </div>


      {/* =================================================
          SUCCESS MAIN
      ================================================= */}

      <section className="theme9-success-main">

        <div className="theme9-success-card">


          {/* =================================================
              TOP DECORATION
          ================================================= */}

          <div className="theme9-success-card-accent">
          </div>


          {/* =================================================
              SUCCESS ICON AREA
          ================================================= */}

          <div className="theme9-success-icon-wrap">


            <div className="theme9-success-icon-ring">

              <div className="theme9-success-icon">

                <Check
                  size={32}
                  strokeWidth={3}
                />

              </div>

            </div>


            <span className="theme9-success-leaf theme9-success-leaf-left">

              <Leaf
                size={18}
              />

            </span>


            <span className="theme9-success-leaf theme9-success-leaf-right">

              <Leaf
                size={15}
              />

            </span>


          </div>


          {/* =================================================
              LABEL
          ================================================= */}

          <div className="theme9-success-label">

            <CheckCircle2
              size={14}
            />

            <span>
              ORDER CONFIRMED
            </span>

          </div>


          {/* =================================================
              TITLE
          ================================================= */}

          <h1>
            Thank You!
          </h1>


          {/* =================================================
              MESSAGE
          ================================================= */}

          <p className="theme9-success-message">

            Your order has been received
            and is being freshly prepared
            for your table.

          </p>


          {/* =================================================
              ORDER NUMBER
          ================================================= */}

          <div className="theme9-success-order">


            <div className="theme9-success-order-icon">

              <ReceiptText
                size={18}
              />

            </div>


            <div>

              <span>
                YOUR ORDER
              </span>

              <strong>

                {orderNumber ||
                  `#${orderId || "—"}`}

              </strong>

            </div>


            <div className="theme9-success-order-status">

              <span>
                CONFIRMED
              </span>

              <CheckCircle2
                size={15}
              />

            </div>


          </div>


          {/* =================================================
              ORDER DETAILS
          ================================================= */}

          <div className="theme9-success-details">


            {/* =================================================
                TABLE
            ================================================= */}

            <div className="theme9-success-detail">

              <div className="theme9-success-detail-icon">

                <Utensils
                  size={17}
                />

              </div>


              <span>
                TABLE
              </span>


              <strong>
                {tableId}
              </strong>

            </div>


            {/* =================================================
                PAYMENT
            ================================================= */}

            <div className="theme9-success-detail">

              <div className="theme9-success-detail-icon">

                {paymentMethod === "ONLINE" ? (

                  <CreditCard
                    size={17}
                  />

                ) : (

                  <Banknote
                    size={17}
                  />

                )}

              </div>


              <span>
                PAYMENT
              </span>


              <strong>
                {paymentLabel}
              </strong>

            </div>


            {/* =================================================
                TOTAL
            ================================================= */}

            <div className="theme9-success-detail">

              <div className="theme9-success-detail-icon">

                <span className="theme9-success-rupee">
                  ₹
                </span>

              </div>


              <span>
                TOTAL
              </span>


              <strong>

                ₹
                {formattedTotal}

              </strong>

            </div>


          </div>


          {/* =================================================
              ONLINE PAYMENT ID
          ================================================= */}

          {paymentMethod === "ONLINE" &&
            paymentId && (

              <div className="theme9-success-payment-id">


                <div className="theme9-success-payment-id-icon">

                  <CreditCard
                    size={16}
                  />

                </div>


                <div>

                  <span>
                    PAYMENT ID
                  </span>

                  <strong>
                    {paymentId}
                  </strong>

                </div>


                <div className="theme9-success-paid-badge">

                  <Check
                    size={11}
                  />

                  PAID

                </div>


              </div>

            )}


          {/* =================================================
              CASH MESSAGE
          ================================================= */}

          {paymentMethod === "CASH" && (

            <div className="theme9-success-cash-message">


              <div className="theme9-success-cash-icon">

                <Banknote
                  size={19}
                />

              </div>


              <div>

                <span>
                  PAY AT TABLE
                </span>

                <p>

                  Please keep your payment ready.
                  You can pay at the table when
                  your order is served.

                </p>

              </div>


            </div>

          )}


          {/* =================================================
              FRESH PREPARATION MESSAGE
          ================================================= */}

          <div className="theme9-success-fresh-message">


            <span>

              <Leaf
                size={15}
              />

            </span>


            <div>

              <strong>
                Freshly prepared for you
              </strong>

              <p>
                Our kitchen is now preparing
                your order.
              </p>

            </div>


          </div>


          {/* =================================================
              BACK TO MENU
          ================================================= */}

          <button
            type="button"
            className="theme9-success-home-button"
            onClick={handleBackToMenu}
          >

            <span>
              Back to Menu
            </span>

            <ArrowRight
              size={18}
            />

          </button>


          {/* =================================================
              REDIRECT MESSAGE
          ================================================= */}

          <div className="theme9-success-redirect">

            <span className="theme9-success-redirect-dot">
            </span>

            <p>

              You will be returned to the menu
              automatically in a few seconds.

            </p>

          </div>


        </div>

      </section>


      {/* =================================================
          BRAND FOOTER
      ================================================= */}

      <footer className="theme9-success-footer">


        <div className="theme9-success-footer-brand">


          <div className="theme9-success-footer-logo">

            <Leaf
              size={18}
            />

          </div>


          <div>

            <span>
              YUM!
            </span>

            <small>
              FRESH & HEALTHY
            </small>

          </div>


        </div>


        <p>

          Fresh ingredients

          <span>
            ·
          </span>

          Healthy choices

          <span>
            ·
          </span>

          Delicious moments

        </p>


        <div className="theme9-success-footer-fresh">

          <Sparkles
            size={12}
          />

          Made fresh for you

        </div>


      </footer>


    </main>

  );

}


export default SuccessScreen;